
import { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';

export default function BookingSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    eventDate: '',
    eventTime: '',
    rentalDuration: '',
    location: '',
    guestCount: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [activeTab, setActiveTab] = useState<'form' | 'calendar'>('form');

  useEffect(() => {
    // Load Calendly script when calendar tab is active
    if (activeTab === 'calendar') {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.body.appendChild(script);

      return () => {
        // Cleanup script when component unmounts or tab changes
        const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
        if (existingScript) {
          document.body.removeChild(existingScript);
        }
      };
    }
  }, [activeTab]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3681atg03sqb4hjgssg', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          eventType: '',
          eventDate: '',
          eventTime: '',
          rentalDuration: '',
          location: '',
          guestCount: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="booking" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Book Your Event
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Ready to bring golf excitement to your next event? Choose your preferred booking method below.
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="max-w-md mx-auto mb-12">
          <div className="flex bg-gray-100 rounded-full p-1">
            <button
              onClick={() => setActiveTab('form')}
              className={`flex-1 px-6 py-3 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === 'form'
                  ? 'bg-green-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-file-list-3-line mr-2"></i>
              Request Form
            </button>
            <button
              onClick={() => setActiveTab('calendar')}
              className={`flex-1 px-6 py-3 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === 'calendar'
                  ? 'bg-green-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-calendar-line mr-2"></i>
              Schedule Call
            </button>
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          {activeTab === 'calendar' ? (
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="p-6 bg-green-50 border-b">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Schedule a Consultation</h3>
                <p className="text-gray-600">
                  Book a call with us to discuss your event details and get personalized recommendations.
                </p>
              </div>
              <div className="p-6">
                <div 
                  className="calendly-inline-widget" 
                  data-url="https://calendly.com/greatgolfonthego/new-meeting-1" 
                  style={{ minWidth: '320px', height: '700px' }}
                ></div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Popular Rental Packages</h3>
                <div className="space-y-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <div className="flex items-center mb-3">
                      <i className="ri-time-line text-green-600 text-xl mr-3"></i>
                      <h4 className="text-lg font-semibold text-gray-900">Hourly Tee Times</h4>
                    </div>
                    <p className="text-gray-600 mb-2">2-hour minimum rental</p>
                    <p className="text-sm text-gray-500">Perfect for small groups and casual play</p>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <div className="flex items-center mb-3">
                      <i className="ri-sun-line text-green-600 text-xl mr-3"></i>
                      <h4 className="text-lg font-semibold text-gray-900">Half-Day Rental</h4>
                    </div>
                    <p className="text-gray-600 mb-2">4 hours of golf fun</p>
                    <p className="text-sm text-gray-500">Great for parties and team events</p>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <div className="flex items-center mb-3">
                      <i className="ri-calendar-line text-green-600 text-xl mr-3"></i>
                      <h4 className="text-lg font-semibold text-gray-900">Full-Day Rental</h4>
                    </div>
                    <p className="text-gray-600 mb-2">8 hours of premium experience</p>
                      <p className="text-sm text-gray-500">Ideal for tournaments and corporate events</p>
                  </div>
                </div>

                <div className="mt-8 p-6 bg-amber-50 border border-amber-200 rounded-lg">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Requirements</h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-amber-600 mr-2 mt-0.5"></i>
                      High-speed internet connection
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-amber-600 mr-2 mt-0.5"></i>
                      Standard electrical outlet
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-amber-600 mr-2 mt-0.5"></i>
                      Minimum space: 15ft × 15ft × 10ft high
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-amber-600 mr-2 mt-0.5"></i>
                      Ideal surfaces: patios, driveways, lanais
                    </li>
                  </ul>
                </div>
              </div>

              <div>
                <form id="booking-form" data-readdy-form onSubmit={handleSubmit} className="bg-gray-50 rounded-xl p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Request Booking</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Event Type *</label>
                      <div className="relative">
                        <select
                          name="eventType"
                          value={formData.eventType}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm appearance-none"
                        >
                          <option value="">Select event type</option>
                          <option value="corporate">Corporate Event</option>
                          <option value="fundraiser">Fundraiser</option>
                          <option value="birthday">Birthday Party</option>
                          <option value="community">Community Event</option>
                          <option value="private">Private Party</option>
                          <option value="other">Other</option>
                        </select>
                        <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Event Date *</label>
                      <input
                        type="date"
                        name="eventDate"
                        value={formData.eventDate}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Time</label>
                      <input
                        type="time"
                        name="eventTime"
                        value={formData.eventTime}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Rental Duration *</label>
                      <div className="relative">
                        <select
                          name="rentalDuration"
                          value={formData.rentalDuration}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm appearance-none"
                        >
                          <option value="">Select duration</option>
                          <option value="2-hours">2 Hours (Minimum)</option>
                          <option value="3-hours">3 Hours</option>
                          <option value="4-hours">4 Hours (Half Day)</option>
                          <option value="6-hours">6 Hours</option>
                          <option value="8-hours">8 Hours (Full Day)</option>
                          <option value="custom">Custom Duration</option>
                        </select>
                        <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Expected Guests</label>
                      <input
                        type="number"
                        name="guestCount"
                        value={formData.guestCount}
                        onChange={handleInputChange}
                        min="1"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                        placeholder="Number of guests"
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Event Location *</label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                      placeholder="Event address or general location"
                    />
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Additional Details</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm resize-none"
                      placeholder="Tell us more about your event, special requests, or questions..."
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center">
                        <i className="ri-check-circle-line text-green-600 mr-2"></i>
                        <p className="text-green-800">Thank you! We'll contact you within 24 hours to confirm your booking.</p>
                      </div>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center">
                        <i className="ri-error-warning-line text-red-600 mr-2"></i>
                        <p className="text-red-800">Sorry, there was an error submitting your request. Please try again.</p>
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full"
                    size="lg"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line mr-2 animate-spin"></i>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line mr-2"></i>
                        Submit Booking Request
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
